import {
    AllFiltersDrawerClosedReason,
    useTrackAllFiltersDrawerClosed,
    useTrackAllFiltersDrawerOpened,
} from '@/utils/amplitude/analytics';
import { allSearchPageFilterChipsAtom } from '@/pages/SearchPageSimplified/atoms/searchPageAtoms';
import { AuctionHouseFilter } from './components/AuctionHouseFilter/AuctionHouseFilter';
import { Button } from '@liveauctioneers/hammer-ui-core/button';
import { BuyingFormatFilter } from './components/BuyingFormatFilter/BuyingFormatFilter';
import { CategoryFilter } from '../CategoryFilter/CategoryFilter';
import { Chip } from '@liveauctioneers/hammer-ui-core/chips';
import { CreatorFilter } from './components/CreatorFilter/CreatorFilter';
import { CurrentBidFilter } from './components/CurrentBidFilter/CurrentBidFilter';
import { CurrentOpenMenu } from '@/pages/SearchPageSimplified/types';
import { DEFAULT_SORT_ORDER } from '@/redux/modules/search/search.types';
import {
    DEFAULT_STATUS_FOR_ARCHIVED_SEARCH,
    DEFAULT_STATUS_FOR_ONLINE_SEARCH,
    parseQueryParams,
} from '@/utils/queryParams';
import { Distance } from '@/types/Distance';
import { Drawer } from '@liveauctioneers/hammer-ui-core/drawer';
import { fetchSearchAggregationsOnDemand } from '@/redux/modules/search/search.actions';
import { FilterAction } from '@/types/SearchFacet';
import { getAggregationsOnDemandStatus, getSearchUiState } from '@/redux/modules/search/search.selectors';
import { KeywordFilter } from '../KeywordFilter/KeywordFilter';
import { Loading } from '@liveauctioneers/hammer-ui-core/loading';
import { LocationFilter } from '../LocationFilter/LocationFilter';
import { MaterialFilter } from './components/MaterialFilter/MaterialFilter';
import { OriginFilter } from './components/OriginFilter/OriginFilter';
import { PriceRangeFilter } from './components/PriceRangeFilter/PriceRangeFilter';
import { RatingFilter } from '../RatingFilter/RatingFilter';
import { SearchSortAndDirection } from '@/types/search/enums/sortTypes';
import { ShippingFilter } from '../ShippingFilter/ShippingFilter';
import { SlidersHorizontal } from '@liveauctioneers/hammer-ui-core/icons';
import { SortFilter } from '../SortFilter/SortFilter';
import { StyleAndPeriodFilter } from './components/StyleAndPeriodFilter/StyleAndPeriodFilter';
import { useAppDispatch, useAppSelector } from '@/redux/hooks';
import { useAtomValue } from 'jotai';
import { useCallback, useRef } from 'react';
import { useGetFormattedSearchResultsText } from '@/pages/SearchPageSimplified/hooks/useGetFormattedSearchResultsText';
import { useIntl } from '@liveauctioneers/hammer-ui-core/intl';
import { useLocation } from 'react-router-dom';
import { useNavigateSearchPageWithParams } from '@/pages/SearchPageSimplified/hooks/useNavigateSearchPageWithParams';
import { useWindowIsMobile } from '@liveauctioneers/hammer-ui-core/providers';

type Props = {
    currentOpenMenu: CurrentOpenMenu;
    distance: Distance;
    isArchivedSearch: boolean;
    onMenuClose: () => void;
    onMenuOpen: (currentMenu: CurrentOpenMenu) => void;
    permittedSortOptions?: SearchSortAndDirection[];
    setShowAllFiltersDrawer: (show: boolean) => void;
    showAllFiltersDrawer: boolean;
};

export const AllFilters = ({
    currentOpenMenu,
    distance,
    isArchivedSearch,
    onMenuClose,
    onMenuOpen,
    permittedSortOptions,
    setShowAllFiltersDrawer,
    showAllFiltersDrawer,
}: Props) => {
    const { formatMessage } = useIntl();
    const isMobile = useWindowIsMobile();
    const dispatch = useAppDispatch();

    const { search } = useLocation();
    const queryParams = parseQueryParams(search);
    const hasTrackedDrawerEvent = useRef(false);

    const { submitted: isSearchSubmitted, totalFound } = useAppSelector(getSearchUiState);
    const aggregationsOnDemandStatus = useAppSelector(getAggregationsOnDemandStatus);
    const showAggregationsLoading = aggregationsOnDemandStatus === 'loading';

    const navigateWithParams = useNavigateSearchPageWithParams();

    const trackAllFiltersDrawerClosed = useTrackAllFiltersDrawerClosed();
    const trackAllFiltersDrawerOpened = useTrackAllFiltersDrawerOpened();

    const handleClose = useCallback(
        (reason: 'backdrop' | 'closeButton' | 'escape' | 'external' | 'viewResults') => {
            if (!hasTrackedDrawerEvent.current && reason !== 'external') {
                let reasonForClose: AllFiltersDrawerClosedReason;
                switch (reason) {
                    case 'backdrop':
                        reasonForClose = AllFiltersDrawerClosedReason.Backdrop;
                        break;
                    case 'closeButton':
                        reasonForClose = AllFiltersDrawerClosedReason.CloseButton;
                        break;
                    case 'escape':
                        reasonForClose = AllFiltersDrawerClosedReason.Escape;
                        break;
                    case 'viewResults':
                        reasonForClose = AllFiltersDrawerClosedReason.ViewResults;
                        break;
                    default:
                        break;
                }
                trackAllFiltersDrawerClosed({ reason: reasonForClose });
                hasTrackedDrawerEvent.current = true;
            }

            window.history.replaceState({}, '');
            setShowAllFiltersDrawer(false);
        },
        [setShowAllFiltersDrawer, trackAllFiltersDrawerClosed]
    );

    const onOpenDrawer = useCallback(() => {
        hasTrackedDrawerEvent.current = false;
        dispatch(fetchSearchAggregationsOnDemand());
        trackAllFiltersDrawerOpened();
        setShowAllFiltersDrawer(true);
    }, [dispatch, trackAllFiltersDrawerOpened, setShowAllFiltersDrawer]);

    const onClearAllFilters = useCallback(() => {
        const searchStatus = isArchivedSearch ? DEFAULT_STATUS_FOR_ARCHIVED_SEARCH : DEFAULT_STATUS_FOR_ONLINE_SEARCH;

        const params: {
            house_id?: string;
            keyword: string;
            sort: SearchSortAndDirection;
            status: typeof DEFAULT_STATUS_FOR_ARCHIVED_SEARCH | typeof DEFAULT_STATUS_FOR_ONLINE_SEARCH;
        } = {
            keyword: queryParams.keyword ?? '',
            sort: DEFAULT_SORT_ORDER,
            status: searchStatus,
        };

        navigateWithParams({
            currentOpenMenu: CurrentOpenMenu.AllFilters,
            filterAction: FilterAction.ClearAllFilters,
            params,
        });
    }, [isArchivedSearch, navigateWithParams, queryParams.keyword]);

    const allFilterChips = useAtomValue(allSearchPageFilterChipsAtom);

    const totalFoundText = useGetFormattedSearchResultsText({ totalFound });

    return (
        <div>
            <Button
                className="mb-md mt-sm hidden w-full xsMax:flex"
                iconLeft={SlidersHorizontal}
                onClick={onOpenDrawer}
                variant="outlined"
            >
                {formatMessage({ id: 'searchPage.mobileHeader.title' })}
            </Button>
            <Chip
                className="xsMax:hidden"
                filterCount={allFilterChips.length}
                onClick={onOpenDrawer}
                style="allFilters"
            >
                {formatMessage({ id: 'searchPage.facets.allFilters' })}
            </Chip>
            <Drawer
                actions={
                    <div className="grid w-full grid-cols-2 gap-xs">
                        <Button
                            onClick={onClearAllFilters}
                            variant="outlined"
                        >
                            {formatMessage({ id: 'clear' })}
                        </Button>
                        <Button
                            loading={isSearchSubmitted}
                            onClick={() => handleClose('viewResults')}
                        >
                            {formatMessage({ id: 'searchPage.facets.viewResults' }, { totalFoundText })}
                        </Button>
                    </div>
                }
                // Higher z-index than feedback widget
                anchor="left"
                className="z-[1001]"
                handleClose={(reason) => handleClose(reason)}
                hasShadow
                headerText="All Filters"
                keepInDOMWhileClosed={false}
                show={showAllFiltersDrawer}
                size="wide"
            >
                <div className="flex flex-col">
                    {showAggregationsLoading ? (
                        <Loading />
                    ) : (
                        <>
                            {isMobile && (
                                <SortFilter
                                    isAllFilters
                                    permittedSortOptions={permittedSortOptions}
                                />
                            )}
                            <KeywordFilter isAllFilters />
                            <CategoryFilter
                                currentOpenMenu={currentOpenMenu}
                                isAllFilters
                                isArchivedSearch={isArchivedSearch}
                                onMenuClose={onMenuClose}
                                onMenuOpen={onMenuOpen}
                            />
                            <LocationFilter
                                currentOpenMenu={currentOpenMenu}
                                distance={distance}
                                isAllFilters
                                isArchivedSearch={isArchivedSearch}
                                onMenuClose={onMenuClose}
                                onMenuOpen={onMenuOpen}
                            />
                            <ShippingFilter
                                currentOpenMenu={currentOpenMenu}
                                isAllFilters
                                isArchivedSearch={isArchivedSearch}
                                onMenuClose={onMenuClose}
                                onMenuOpen={onMenuOpen}
                            />
                            <RatingFilter
                                isAllFilters
                                isArchivedSearch={isArchivedSearch}
                            />
                            {!isArchivedSearch && <BuyingFormatFilter />}
                            <AuctionHouseFilter isArchivedSearch={isArchivedSearch} />
                            <CurrentBidFilter isArchivedSearch={isArchivedSearch} />
                            <PriceRangeFilter isArchivedSearch={isArchivedSearch} />
                            <OriginFilter isArchivedSearch={isArchivedSearch} />
                            <MaterialFilter isArchivedSearch={isArchivedSearch} />
                            <StyleAndPeriodFilter isArchivedSearch={isArchivedSearch} />
                            <CreatorFilter isArchivedSearch={isArchivedSearch} />
                        </>
                    )}
                </div>
            </Drawer>
        </div>
    );
};
